import React, { Fragment, useEffect, useState, useCallback } from "react";
import { Form, FormGroup, Input, Label, Button, Col } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { registerRequest, reset } from "../../redux/auth/register/actions";
import { showToast } from "../../Components/Common/Toast";
import { fetchCompanyRequest } from "../../redux/company/actions";
import CreatableSelect from "react-select/creatable";
import Editpersonal from "./Editpersonal";
import { scanResumeRequest } from "../../redux/jobseeker/scan_resume/actions";
import { bulkUploadRequest } from "../../redux/recruiter/candidate_bulk_upload/actions";
import { returnParsedResumeRequest } from "../../redux/auth/return_parse_resume/actions";
import logoWhite from "../../assets/images/logo/logo.png";
import Cookies from "universal-cookie";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import { PENDING_RECRUITER_CAMPAIGN_PUBLIC_TOKEN_KEY } from "../Public/RecruiterCampaignInviteLanding";

const RegisterForm = () => {
  const dispatch = useDispatch();
  const [togglePassword, setTogglePassword] = useState(false);
  const { type, token } = useParams();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [companyOptions, setCompanyOptions] = useState([]);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [steps, setSteps] = useState(1);
  const [invitationData, setInvitationData] = useState(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState(null);
  const [pendingCampaignPublicToken, setPendingCampaignPublicToken] =
    useState(null);
  const cookies = new Cookies();
  const getDomain = () => {
    const hostname = window.location.hostname;
    if (hostname === "localhost") {
      return "localhost"; // for local development
    } else if (hostname.endsWith("naukrify.com")) {
      return ".naukrify.com"; // for naukrify.com subdomains
    }
    return hostname;
  };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    company: null,
    type: type ? type.toUpperCase() : "",
  });

  useEffect(() => {
    setFormData({
      name: "",
      email: "",
      password: "",
      company: null,
      type: type ? type.toUpperCase() : "",
    });
    setErrors({});
  }, [type]);

  useEffect(() => {
    if (!token || type?.toUpperCase() !== "RECRUITER") return;
    setInvitationLoading(true);
    setInvitationError(null);
    AxiosInstance()
      .post("/verify_invitation_token", { token })
      .then((res) => {
        if (res.data.status && !res.data.isAlreadyAccepted && res.data.invite_email) {
          const companyId = res.data.company_id;
          const companyName = (res.data.company_name || "").trim();
          const hasInvitationCompany = companyId && companyName && companyName !== "Invited Recruiter";
          setInvitationData({
            invite_email: res.data.invite_email,
            company_name: companyName || "",
            company_id: companyId,
            canSelectCompany: !hasInvitationCompany,
          });
          setFormData((prev) => ({
            ...prev,
            email: res.data.invite_email,
            company: hasInvitationCompany ? { value: companyId, label: companyName } : null,
            type: "RECRUITER",
          }));
        } else if (res.data.isAlreadyAccepted) {
          setInvitationError("This invitation link has already been used.");
        } else {
          setInvitationError(res.data.message || "This invitation link is invalid or already used.");
        }
      })
      .catch(() => {
        setInvitationError("This invitation link is invalid or already used.");
      })
      .finally(() => {
        setInvitationLoading(false);
      });
  }, [token, type]);

  useEffect(() => {
    if (token) {
      setPendingCampaignPublicToken(null);
      try {
        localStorage.removeItem(PENDING_RECRUITER_CAMPAIGN_PUBLIC_TOKEN_KEY);
      } catch (e) {
        /* ignore */
      }
      return;
    }
    if (type?.toUpperCase() !== "RECRUITER") {
      setPendingCampaignPublicToken(null);
      return;
    }
    try {
      const raw = localStorage.getItem(
        PENDING_RECRUITER_CAMPAIGN_PUBLIC_TOKEN_KEY
      );
      const t = raw ? String(raw).trim().toLowerCase() : "";
      setPendingCampaignPublicToken(
        t.length >= 32 && t.length <= 64 && /^[a-f0-9]+$/i.test(t) ? t : null
      );
    } catch (e) {
      setPendingCampaignPublicToken(null);
    }
  }, [token, type]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      const useInvitationCompany = token && invitationData && !invitationData.canSelectCompany;
      const formattedData = {
        name: formData.name || "",
        email: token && invitationData ? invitationData.invite_email : formData.email || "",
        password: formData.password || "",
        company_id: useInvitationCompany ? invitationData.company_id : (formData.company?.__isNew__ ? "" : formData.company?.value),
        company_name: useInvitationCompany ? invitationData.company_name : (formData.company?.label || ""),
        is_new: useInvitationCompany ? false : (formData.company?.__isNew__ || false),
        type: formData.type || "RECRUITER",
        token: token || undefined,
        ...(pendingCampaignPublicToken && !token
          ? { campaign_public_token: pendingCampaignPublicToken }
          : {}),
      };

      dispatch(registerRequest(formattedData));
    }
  };

  const companyReducerData = useSelector((state) => state.companyReducer);
  const companyData = companyReducerData.data.data;

  useEffect(() => {
    if (companyData && companyData.length > 0) {
      setCompanyOptions(companyData);
    }
  }, [companyData]);

  const registerReducerData = useSelector((state) => state.registerReducer);
  useEffect(() => {
    if (registerReducerData.status && registerReducerData.message !== "") {
      showToast("success", registerReducerData.message);
      dispatch(reset());

      // setTimeout(() => {
      //   navigate(`${process.env.PUBLIC_URL}/${type}/auth/login/`);
      // }, 2000);

      const user_data = {
        name: registerReducerData.data?.name || "", // Use optional chaining
        email: registerReducerData.data?.email || "", // Use optional chaining
        type: registerReducerData.data?.type || "", // Use optional chaining
      };
      localStorage.setItem("_token", registerReducerData.data?.token || ""); // Use optional chaining
      localStorage.setItem("user_data", JSON.stringify(user_data));
      if (type?.toUpperCase() === "RECRUITER") {
        try {
          localStorage.removeItem(PENDING_RECRUITER_CAMPAIGN_PUBLIC_TOKEN_KEY);
        } catch (e) {
          /* ignore */
        }
      }
      if (type.toUpperCase() == "JOBSEEKER") {
        if (
          registerReducerData.data?.experience &&
          registerReducerData.data?.eduction &&
          registerReducerData.data?.skills
        ) {
          setTimeout(() => {
            navigate(`${process.env.PUBLIC_URL}/${type}/dashboard`);
          }, 2000);
        } else {
          setTimeout(() => {
            navigate(`${process.env.PUBLIC_URL}/${type}/update_profile`);
          }, 2000);
        }
      } else {
        setTimeout(() => {
          navigate(`${process.env.PUBLIC_URL}/${type}/auth/login/`);
        }, 2000);
      }
    } else if (
      registerReducerData.status === false &&
      registerReducerData.message !== ""
    ) {
      showToast("error", registerReducerData.message);
      dispatch(reset());
    }
  }, [registerReducerData]);

  const returnParseResumeReducer = useSelector(
    (state) => state.returnParseResumeReducer
  );

  const returnParseResumeReducerData = returnParseResumeReducer.data;

  useEffect(() => {
    if (
      returnParseResumeReducerData &&
      returnParseResumeReducerData.length > 0
    ) {
      handleNextClick();
      // Assuming the first object in the array contains the parsed resume data
      const parsedData = returnParseResumeReducerData[0];

      // Extract relevant fields from the parsed resume data
      const { email, name, phone, location, experience, skills } = parsedData;

      cookies.set("parsedResumeData", parsedData, {
        path: "/",
        domain: getDomain(),
        sameSite: "None",
        secure: true,
      });
      // Cookies.set("parsedResumeData", parsedData, {
      //   expires: 30,
      // });
      // Update formData with parsed data
      setFormData((prevData) => ({
        ...prevData,
        email: email || prevData.email, // Use existing email if not available
        name: name || prevData.name, // Use existing name if not available
        phone: phone || prevData.phone, // Add phone to formData
        location: location || prevData.location, // Add location to formData
        experience: experience || prevData.experience, // Add experience to formData
        skills: skills || prevData.skills, // Add skills to formData
      }));
    }
  }, [returnParseResumeReducerData]);

  const validate = () => {
    let errors = {};
    let isValid = true;
    const isInviteFlow = token && type?.toUpperCase() === "RECRUITER";

    const inviteNoCompany = isInviteFlow && invitationData?.canSelectCompany;
    if (
      (type?.toUpperCase() === "RECRUITER" && !formData.company) &&
      (!isInviteFlow || inviteNoCompany)
    ) {
      errors.company = "Company is required";
      isValid = false;
    }

    if (!formData.name) {
      errors.name = "Name is required";
      isValid = false;
    }

    if (!isInviteFlow) {
      if (!formData.email) {
        errors.email = "Email is required";
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,6}$/;
        if (!emailRegex.test(formData.email)) {
          errors.email = "Please enter a valid email";
          isValid = false;
        }
      }
    }

    if (!formData.password) {
      errors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters long";
      isValid = false;
    } else if (!/[a-z]/.test(formData.password)) {
      errors.password = "Password must contain at least one lowercase letter";
      isValid = false;
    } else if (!/[A-Z]/.test(formData.password)) {
      errors.password = "Password must contain at least one uppercase letter";
      isValid = false;
    } else if (!/\d/.test(formData.password)) {
      errors.password = "Password must contain at least one digit";
      isValid = false;
    } else if (!/[^a-zA-Z\d]/.test(formData.password)) {
      errors.password =
        "Password must contain at least one special character";
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
      // setUploadStatus("loading");
    }
  };

  const handleScanNow = (e) => {
    if (resumeFile) {
      console.log("resumeFile", resumeFile);
      const submissionData = new FormData();
      submissionData.append("jobseeker_resume_file", resumeFile);

      console.log("submissionData", submissionData);
      dispatch(returnParsedResumeRequest(submissionData));
    } else {
      showToast("error", "Please upload a file before scanning");
    }
  };

  const handleNextClick = () => {
    setSteps((prev) => prev + 1);
  };

  const handleCompanyFocus = () => {
    const fetchData = async () => {
      try {
        const keyword = "";
        dispatch(fetchCompanyRequest(keyword));
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  };

  const handleCompanyInputChange = useCallback((inputValue) => {
    const fetchData = async () => {
      try {
        dispatch(fetchCompanyRequest(inputValue));
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, []);

  const handleCompanyChange = (selectedOption) => {
    const updatedErrors = { ...errors, company: "" };
    setErrors(updatedErrors);
    setFormData({ ...formData, company: selectedOption });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors({ ...errors, [name]: "" });
    setFormData({ ...formData, [name]: value });
  };

  return (
    <Fragment>
      <Col
        xl="5"
        className="b-center bg-size"
        style={{
          backgroundImage: `url(${require("../../assets/images/login/office.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: steps === 1 || steps === 2 ? "block" : "none",
        }}
      ></Col>
      <Col xl={steps === 1 || steps === 2 ? "7 p-0" : "12 p-0"}>
        <div className="login-card">

          <div className="login-main">
            <Link className="logo" to={process.env.PUBLIC_URL}>
              <img
                className="img-fluid for-light"
                src={logoWhite}
                alt="loginpage"
                style={{ width: "60%" }}
              />
            </Link>
            {type.toUpperCase() === "JOBSEEKER" ? (
              <div>
                {steps === 1 && (
                  <>
                    <h4>Upload Your Resume</h4>
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeUpload}
                    />
                    <Button
                      className="mt-2"
                      color="primary"
                      onClick={handleScanNow}
                      disabled={returnParseResumeReducer.isLoading}
                    >
                      Upload Resume
                    </Button>
                  </>
                )}

                {steps === 2 && (
                  <Form
                    className="theme-form login-form"
                    onSubmit={handleSubmit}
                  >
                    <h4>Create Jobseeker account</h4>
                    <p>Enter your personal details to create account</p>
                    <FormGroup>
                      <Label className="col-form-label m-0 pt-0">
                        Your Name<span className="text-danger">*</span>
                      </Label>
                      <Input
                        className="form-control"
                        type="text"
                        name="name"
                        placeholder="Full Name"
                        onChange={handleChange}
                        value={formData.name}
                      />
                      {errors.name && (
                        <small className="text-danger">{errors.name}</small>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label className="col-form-label m-0 pt-0">
                        Email Address<span className="text-danger">*</span>
                      </Label>
                      <Input
                        className="form-control"
                        type="text"
                        name="email"
                        placeholder="abc@gmail.com"
                        onChange={handleChange}
                        value={formData.email}
                        maxLength={50}
                      />
                      {errors.email && (
                        <small className="text-danger">{errors.email}</small>
                      )}
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label className="col-form-label m-0 pt-0">
                        Password<span className="text-danger">*</span>
                      </Label>
                      <div className="position-relative">
                        <Input
                          className="form-control"
                          type={togglePassword ? "text" : "password"}
                          name="password"
                          placeholder="********"
                          onChange={handleChange}
                        />
                        <div
                          className="show-hide"
                          onClick={() => setTogglePassword(!togglePassword)}
                        >
                          <span className={togglePassword ? "" : "show"}></span>
                        </div>
                      </div>
                      {errors.password && (
                        <small className="text-danger">{errors.password}</small>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Button
                        disabled={registerReducerData.isLoading}
                        className="d-block w-100"
                        color="primary"
                        type="submit"
                      >
                        Create Account
                      </Button>
                    </FormGroup>
                    <p class Name="mb-0 text-start">
                      Already have an account?
                      <Link
                        className="ms-2"
                        to={`${process.env.PUBLIC_URL}/jobseeker/auth/login`}
                      >
                        Sign in
                      </Link>
                    </p>
                  </Form>
                )}
              </div>
            ) : (
              <Form className="theme-form login-form" onSubmit={handleSubmit}>
                <h4>Create {type} account</h4>
                <p>Enter your personal details to create account</p>

                {token && type?.toUpperCase() === "RECRUITER" && (
                  <>
                    {invitationLoading && (
                      <div className="text-center py-3">
                        <Spinner />
                        <p className="mt-2 small text-muted">Verifying invitation...</p>
                      </div>
                    )}
                    {invitationError && !invitationLoading && (
                      <div className="alert alert-danger">
                        {invitationError}
                        <Link className="ms-2" to={`${process.env.PUBLIC_URL}/recruiter/auth/login`}>
                          Go to login
                        </Link>
                      </div>
                    )}
                  </>
                )}

                {type.toUpperCase() === "RECRUITER" && token == null && (
                  <FormGroup>
                    <Label className="col-form-label m-0 pt-0">
                      Your Company<span className="text-danger">*</span>
                    </Label>
                    <CreatableSelect
                      isClearable
                      className="mt-2"
                      options={companyOptions}
                      isLoading={companyReducerData.isLoading}
                      placeholder="Select Company..."
                      value={formData.company}
                      onChange={handleCompanyChange}
                      onInputChange={handleCompanyInputChange}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          backgroundColor: "#f3f3ff",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          height: "46px",
                          padding: "0px",
                          boxShadow: state.isFocused
                            ? "0 0 0 2px rgba(0, 123, 255, 0.2)"
                            : "none",
                          "&:hover": {
                            borderColor: "#999",
                          },
                        }),
                      }}
                      onFocus={handleCompanyFocus}
                    />
                    {errors.company && (
                      <small className="text-danger">{errors.company}</small>
                    )}
                  </FormGroup>
                )}

                {token && invitationData && !invitationLoading && !invitationError && (
                  <>
                    <FormGroup>
                      <Label className="col-form-label m-0 pt-0">Email</Label>
                      <Input
                        className="form-control bg-light text-dark"
                        type="text"
                        value={formData.email}
                        readOnly
                        style={{ color: "#212529", opacity: 1 }}
                      />
                    </FormGroup>
                    {invitationData.canSelectCompany ? (
                      <FormGroup>
                        <Label className="col-form-label m-0 pt-0">
                          Company<span className="text-danger">*</span>
                        </Label>
                        <CreatableSelect
                          isClearable
                          className="mt-2"
                          options={companyOptions}
                          isLoading={companyReducerData.isLoading}
                          placeholder="Select or type to search company..."
                          value={formData.company}
                          onChange={handleCompanyChange}
                          onInputChange={handleCompanyInputChange}
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              backgroundColor: "#fff",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              height: "46px",
                              padding: "0px",
                              boxShadow: state.isFocused
                                ? "0 0 0 2px rgba(0, 123, 255, 0.2)"
                                : "none",
                              "&:hover": {
                                borderColor: "#999",
                              },
                            }),
                          }}
                          onFocus={handleCompanyFocus}
                        />
                        {errors.company && (
                          <small className="text-danger">{errors.company}</small>
                        )}
                      </FormGroup>
                    ) : (
                      <FormGroup>
                        <Label className="col-form-label m-0 pt-0">Company</Label>
                        <Input
                          className="form-control bg-light text-dark"
                          type="text"
                          value={invitationData.company_name}
                          readOnly
                          style={{ color: "#212529", opacity: 1 }}
                        />
                      </FormGroup>
                    )}
                    <input type="hidden" name="invitation_token" value={token} />
                  </>
                )}

                {(!token || (token && invitationData && !invitationError)) && (
                  <>
                    <FormGroup>
                      <Label className="col-form-label m-0 pt-0">
                        Your Name<span className="text-danger">*</span>
                      </Label>
                      <Input
                        className="form-control"
                        type="text"
                        name="name"
                        placeholder="Full Name"
                        onChange={handleChange}
                        value={formData.name}
                      />
                      {errors.name && (
                        <small className="text-danger">{errors.name}</small>
                      )}
                    </FormGroup>
                    {token == null && (
                      <FormGroup>
                        <Label className="col-form-label m-0 pt-0">
                          Email Address<span className="text-danger">*</span>
                        </Label>
                        <Input
                          className="form-control"
                          type="text"
                          name="email"
                          placeholder="abc@gmail.com"
                          onChange={handleChange}
                          value={formData.email}
                          maxLength={50}
                        />
                        {errors.email && (
                          <small className="text-danger">{errors.email}</small>
                        )}
                      </FormGroup>
                    )}
                    <FormGroup className="position-relative">
                      <Label className="col-form-label m-0 pt-0">
                        Password<span className="text-danger">*</span>
                      </Label>
                      <div className="position-relative">
                        <Input
                          className="form-control"
                          type={togglePassword ? "text" : "password"}
                          name="password"
                          placeholder="********"
                          onChange={handleChange}
                        />
                        <div
                          className="show-hide"
                          onClick={() => setTogglePassword(!togglePassword)}
                        >
                          <span className={togglePassword ? "" : "show"}></span>
                        </div>
                      </div>
                      {errors.password && (
                        <small className="text-danger">{errors.password}</small>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Button
                        disabled={registerReducerData.isLoading}
                        className="d-block w-100"
                        color="primary"
                        type="submit"
                      >
                        Create Account
                      </Button>
                    </FormGroup>
                    <p className="mb-0 text-start">
                      Already have an account?
                      <Link
                        className="ms-2"
                        to={`${process.env.PUBLIC_URL}/${type.toLowerCase()}/auth/login`}
                      >
                        Sign in
                      </Link>
                    </p>
                    <p className="mb-0 text-start">
                      Create{" "}
                      {type.toUpperCase() === "RECRUITER"
                        ? "jobseeker"
                        : "recruiter"}{" "}
                      account
                      <Link
                        className="ms-2"
                        to={`${process.env.PUBLIC_URL}/${type.toUpperCase() === "RECRUITER"
                          ? "jobseeker"
                          : "recruiter"
                        }/auth/${type.toUpperCase() === "RECRUITER"
                          ? "login"
                          : "register"
                        }`}
                      >
                        Create Account
                      </Link>
                    </p>
                  </>
                )}
              </Form>
            )}
          </div>
        </div>
      </Col>
    </Fragment>
  );
};

export default RegisterForm;
